import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useRecoilState, useSetRecoilState } from "recoil";
import { useTheme } from "../../hooks/useTheme";
import { useStrings } from "../../hooks/useStrings";
import { LAYER_Z_INDEX } from "../../constants/ui";
import { analyticsEvent } from "../../utils/analytics";
import { FilmRequest, helpFilmPlayingState, helpFilmsWatchedState } from "../../states/helpFilmState";
import { chapterTitle, clock, filmCut, filmLanguage, filmLanguages, FilmLanguage, filmPlayerAt, helpFilm } from "./helpFilms";
import { filmTitle } from "./HelpFilm";

/** Watching this long counts as watched: the link then keeps only its length. */
const WATCHED_AFTER_MS = 15_000;
const LANGUAGE_NAMES: Record<FilmLanguage, string> = { en: "English", es: "Español", de: "Deutsch" };

/**
 * The one film player, mounted once at the app's root so it sits above every dialog and keeps playing when it moves
 * to the corner or the page changes. A link only asks for a film; this plays it.
 */
export function HelpFilmHost() {
    const [request, setRequest] = useRecoilState(helpFilmPlayingState);
    if (!request) {
        return null;
    }
    return <FilmWindow key={`${request.film}:${request.part.from}:${request.place}`} request={request} onClose={() => setRequest(undefined)}/>;
}

/**
 * Stream's player with the film's chapters beside it. It starts at the part's chapter, in the dashboard's language,
 * with captions on. "Keep playing while I work" moves the same player to the corner: the frame stays in place in the
 * tree, only its box changes, so the video goes on where it was.
 */
function FilmWindow({ request, onClose }: { request: FilmRequest; onClose: () => void }) {
    const { css, theme } = useTheme();
    const { strings, format } = useStrings();
    const film = helpFilm(request.film);
    const [language, setLanguage] = useState<FilmLanguage>(() => filmLanguage(film, strings.getLanguage()));
    const [chapter, setChapter] = useState(request.part.from);
    const [corner, setCorner] = useState(false);
    const setWatched = useSetRecoilState(helpFilmsWatchedState);
    const closeRef = useRef<HTMLButtonElement>(null);
    const cut = filmCut(film, language);
    const title = filmTitle(strings, film.id);
    const src = filmPlayerAt(film, language, cut.chapters[chapter] ?? 0);
    const languages = filmLanguages(film);

    useEffect(() => {
        if (film.id === "setup") {
            analyticsEvent("setup_film_open", film.id);
        } else {
            analyticsEvent("help_film_open", `${film.id}:${request.part.from}:${request.place}`);
        }
        const timer = window.setTimeout(() => {
            setWatched((watched) => ({ ...watched, [`${film.id}:${request.part.from}`]: Date.now() }));
        }, WATCHED_AFTER_MS);
        return () => window.clearTimeout(timer);
        // once per film opened (the host keys this window by the request)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (corner) {
            return;
        }
        closeRef.current?.focus();
        const onKey = (event: KeyboardEvent) => {
            if (event.key === "Escape") {
                event.stopPropagation();
                onClose();
            }
        };
        // capture, so Escape closes the film and not the dialog under it
        window.addEventListener("keydown", onKey, true);
        return () => window.removeEventListener("keydown", onKey, true);
    }, [corner, onClose]);

    const iconButton = css({
        width: "32px", height: "32px", display: "grid", placeItems: "center", flexShrink: 0, border: "none", borderRadius: "8px",
        background: "transparent", cursor: "pointer", color: theme.colors.contentPrimary,
        ":hover": { backgroundColor: theme.customColors.translucent },
        ":focus-visible": { outline: `2px solid ${theme.colors.accent}`, outlineOffset: "2px" },
    });
    const quiet = css({
        ...theme.typography.ParagraphXSmall, justifySelf: "start", textAlign: "left", background: "none", border: "none", padding: "6px 8px 0",
        cursor: "pointer", color: theme.colors.contentSecondary, textDecoration: "underline", textDecorationColor: theme.customColors.hairline,
        textUnderlineOffset: "3px", ":hover": { color: theme.colors.contentPrimary, textDecorationColor: "currentColor" },
        ":focus-visible": { outline: `2px solid ${theme.colors.accent}`, outlineOffset: "2px" },
    });
    const chapterTitles = film.chapters.map((_, i) => chapterTitle(film, i, language));
    const playingTitle = chapterTitles[chapter] || title;

    return createPortal(
        <div className={css({ position: "fixed", inset: 0, zIndex: LAYER_Z_INDEX + 100, pointerEvents: corner ? "none" : "auto" })}>
            {!corner && <div className={css({ position: "absolute", inset: 0, backgroundColor: "rgba(0, 0, 0, .5)" })} onClick={onClose}/>}
            <div role="dialog" aria-label={title} className={css(corner ? {
                position: "absolute", right: "16px", bottom: "16px", width: "min(320px, calc(100vw - 32px))", pointerEvents: "auto",
                backgroundColor: theme.colors.primaryB, color: theme.colors.contentPrimary, borderRadius: "12px", overflow: "hidden",
                boxShadow: theme.lighting.shadow600, border: `1px solid ${theme.customColors.hairline}`,
            } : {
                position: "absolute", left: "50%", top: "50%", transform: "translate(-50%, -50%)", width: "min(960px, calc(100vw - 32px))",
                maxHeight: "calc(100vh - 32px)", overflowY: "auto", boxSizing: "border-box", padding: "18px 20px 20px",
                backgroundColor: theme.colors.primaryB, color: theme.colors.contentPrimary, borderRadius: "16px",
                display: "grid", gap: "12px",
            })}>
                {!corner && (
                    <div className={css({ display: "flex", alignItems: "center", gap: "10px" })}>
                        <span className={css({ display: "flex", alignItems: "baseline", gap: "10px", flex: 1, minWidth: 0, flexWrap: "wrap" })}>
                            <span className={css({ ...theme.typography.LabelLarge })}>{title}</span>
                            <span className={css({ ...theme.typography.ParagraphSmall, color: theme.colors.contentTertiary })}>{format(strings.Setup.VideoLength, clock(cut.length))}</span>
                        </span>
                        <button ref={closeRef} type="button" aria-label={strings.General.Close} onClick={onClose} className={iconButton}><CloseIcon/></button>
                    </div>
                )}
                <div className={css(corner ? { display: "block" } : {
                    display: "grid", gridTemplateColumns: chapterTitles.length > 0 || languages.length > 1 ? "minmax(0, 1fr) 236px" : "minmax(0, 1fr)",
                    gap: "20px", alignItems: "start",
                    "@media (max-width: 760px)": { gridTemplateColumns: "minmax(0, 1fr)" },
                })}>
                    <div className={css({ position: "relative", width: "100%", aspectRatio: "16 / 9", borderRadius: corner ? 0 : "8px", overflow: "hidden", backgroundColor: "#000" })}>
                        <iframe
                            key={src}
                            title={title}
                            src={src}
                            style={{ position: "absolute", inset: 0, width: "100%", height: "100%", border: "none" }}
                            allow="accelerometer; gyroscope; autoplay; encrypted-media; picture-in-picture"
                            allowFullScreen
                        />
                    </div>
                    {!corner && (chapterTitles.length > 0 || languages.length > 1) && (
                        <div className={css({ display: "grid", gap: "2px", alignContent: "start" })}>
                            {chapterTitles.length > 0 && (
                                <>
                                    <span className={css({ ...theme.typography.LabelXSmall, color: theme.colors.contentTertiary, padding: "0 8px 6px" })}>{strings.HelpFilms.Chapters}</span>
                                    {chapterTitles.map((name, i) => (
                                        <button key={i} type="button" onClick={() => setChapter(i)} aria-current={i === chapter ? "true" : undefined} className={css({
                                            display: "grid", gridTemplateColumns: "38px minmax(0, 1fr)", gap: "6px", padding: "6px 8px", textAlign: "left",
                                            border: "none", borderRadius: "6px", cursor: "pointer", color: theme.colors.contentPrimary,
                                            ...theme.typography.ParagraphXSmall, fontWeight: i === chapter ? 500 : 400,
                                            backgroundColor: i === chapter ? theme.customColors.translucentStrong : "transparent",
                                            ":hover": { backgroundColor: i === chapter ? theme.customColors.translucentStrong : theme.customColors.translucent },
                                            ":focus-visible": { outline: `2px solid ${theme.colors.accent}`, outlineOffset: "-2px" },
                                        })}>
                                            <span className={css({ color: theme.colors.contentTertiary, fontVariantNumeric: "tabular-nums", fontWeight: 400 })}>{clock(cut.chapters[i] ?? 0)}</span>
                                            <span>{name}</span>
                                        </button>
                                    ))}
                                    <div className={css({ height: "1px", backgroundColor: theme.customColors.hairline, margin: "10px 8px" })}/>
                                </>
                            )}
                            {languages.length > 1 && (
                                <div role="group" aria-label={strings.HelpFilms.Language} className={css({ display: "flex", gap: "12px", padding: "0 8px" })}>
                                    {languages.map((l) => (
                                        <button key={l} type="button" aria-pressed={l === language} onClick={() => setLanguage(l)} className={css({
                                            ...theme.typography.ParagraphXSmall, background: "none", border: "none", padding: 0, cursor: "pointer",
                                            color: l === language ? theme.colors.contentPrimary : theme.colors.contentSecondary, fontWeight: l === language ? 500 : 400,
                                            ":hover": { color: theme.colors.contentPrimary },
                                            ":focus-visible": { outline: `2px solid ${theme.colors.accent}`, outlineOffset: "2px" },
                                        })}>{LANGUAGE_NAMES[l]}</button>
                                    ))}
                                </div>
                            )}
                            <button type="button" className={quiet} onClick={() => setCorner(true)}>{strings.HelpFilms.KeepPlaying}</button>
                            <a className={quiet} href={strings.Help.Url} target="_blank" rel="noopener noreferrer">{strings.HelpFilms.OpenHelpCenter}</a>
                        </div>
                    )}
                </div>
                {corner && (
                    <div className={css({ display: "flex", alignItems: "center", gap: "4px", padding: "6px 6px 6px 12px" })}>
                        <span className={css({ flex: 1, minWidth: 0, ...theme.typography.LabelXSmall, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" })}>{playingTitle}</span>
                        <button type="button" aria-label={strings.HelpFilms.BackToWindow} title={strings.HelpFilms.BackToWindow} onClick={() => setCorner(false)} className={iconButton}>
                            <svg width="14" height="14" viewBox="0 0 16 16" aria-hidden="true"><path d="M9 3h4v4M13 3L8.5 7.5M7 13H3V9M3 13l4.5-4.5" fill="none" stroke="currentColor" strokeWidth="1.4"/></svg>
                        </button>
                        <button type="button" aria-label={strings.General.Close} title={strings.General.Close} onClick={onClose} className={iconButton}><CloseIcon size={12}/></button>
                    </div>
                )}
            </div>
        </div>,
        document.body,
    );
}

function CloseIcon({ size = 14 }: { size?: number }) {
    return <svg width={size} height={size} viewBox="0 0 14 14" aria-hidden="true"><path d="M2 2l10 10M12 2L2 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/></svg>;
}
